import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

const COMMON_KEYS = ["ios", "android", "web"];

/**
 * `kind: "recordBoolean"` — an OPEN key/boolean map (`PlatformMap`,
 * `Record<string, boolean>`) — e.g. `platforms: {"web": false}`. Free-form key
 * entry (a text field + a small suggestion list of the common `ios`/`android`/
 * `web` tokens) since the schema's key set is deliberately open
 * (docs/207-PLATFORM-AVAILABILITY-FILTER.md — an unrecognized key is simply
 * ignored, never a reason to reject).
 */
export function RecordBooleanEditor({
  label,
  help,
  value,
  onChange,
}: {
  label: string;
  help?: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const [newKey, setNewKey] = useState("");
  const obj: Record<string, boolean> = value && typeof value === "object" ? (value as Record<string, boolean>) : {};
  const keys = Object.keys(obj);

  const commit = (next: Record<string, boolean>) => onChange(Object.keys(next).length > 0 ? next : undefined);
  const setBool = (key: string, v: boolean) => commit({ ...obj, [key]: v });
  const removeKey = (key: string) => {
    const next = { ...obj };
    delete next[key];
    commit(next);
  };
  const addKey = () => {
    const k = newKey.trim();
    if (!k || k in obj) return;
    commit({ ...obj, [k]: false });
    setNewKey("");
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
        {help ? ` — ${help}` : ""}
      </Typography>
      <Stack spacing={1}>
        {keys.map((key) => (
          <Box key={key} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography variant="body2" sx={{ minWidth: 100, fontFamily: "monospace" }}>
              {key}
            </Typography>
            <TextField
              select
              size="small"
              value={obj[key] ? "true" : "false"}
              onChange={(e) => setBool(key, e.target.value === "true")}
              sx={{ minWidth: 110 }}
            >
              <MenuItem value="true">available</MenuItem>
              <MenuItem value="false">hidden</MenuItem>
            </TextField>
            <Button size="small" color="error" onClick={() => removeKey(key)}>
              Remove
            </Button>
          </Box>
        ))}
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            select
            label="Platform key"
            value=""
            onChange={(e) => setNewKey(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            {COMMON_KEYS.filter((k) => !(k in obj)).map((k) => (
              <MenuItem key={k} value={k}>
                {k}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="…or type a key"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            sx={{ minWidth: 140 }}
          />
          <Button size="small" variant="outlined" onClick={addKey} disabled={!newKey.trim()}>
            + Add
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
